import React from "react";
import DocumentMeta from "./DocumentMeta";
import { ROUTE_META } from "./routeMeta";

const sectionClass = "text-2xl font-semibold mt-6 mb-2";
const paragraphClass = "mb-4";

const TermsPDFTools = () => (
  <div className="max-w-3xl mx-auto px-6 py-16 text-gray-800">
    <DocumentMeta
      title={ROUTE_META.pdfToolsTerms.title}
      description={ROUTE_META.pdfToolsTerms.description}
      robots={ROUTE_META.pdfToolsTerms.robots}
    />
    <h1 className="text-4xl font-bold mb-6">Terms of Service for PDF Toolbox by Appazzola</h1>
    <p className={paragraphClass}>
      <strong>Effective date:</strong> April 1, 2026
      <br />
      <strong>Last updated:</strong> April 1, 2026
      <br />
      <strong>App:</strong> PDF Toolbox (the "App")
      <br />
      <strong>Operator:</strong> Appazzola LLC, 231 E Canal St, Hershey, PA 17033 ("Appazzola", "we", "us", "our")
      <br />
      <strong>General contact:</strong> contact@appazzola.com
      <br />
      <strong>Privacy and legal contact:</strong> legal@appazzola.com
    </p>

    <p className={paragraphClass}>
      These Terms govern your use of PDF Toolbox by Appazzola. By using the app, you agree to these Terms.
    </p>

    <h2 className={sectionClass}>Acceptance</h2>
    <p className={paragraphClass}>
      By downloading, accessing, or using PDF Toolbox, you agree to these Terms of Service.
      If you do not agree, do not use the app.
    </p>
    <p className={paragraphClass}>
      If you use the app on behalf of a business or other entity, you represent that you have
      authority to bind that entity to these Terms.
    </p>

    <h2 className={sectionClass}>Eligibility</h2>
    <p className={paragraphClass}>
      You may use the app only if you are legally permitted to enter into this agreement and
      to use the services made available through the app under applicable law.
    </p>

    <h2 className={sectionClass}>The Service</h2>
    <p className={paragraphClass}>
      PDF Toolbox provides mobile PDF utilities such as viewing, merging, splitting, compressing,
      annotating, signing, converting images to PDF, and converting supported text or HTML
      documents to PDF.
    </p>
    <p className={paragraphClass}>
      We may add, remove, improve, or modify features at any time, including free and paid functionality.
    </p>
    <p className={paragraphClass}>
      Features may differ by platform, device capability, operating-system version, region,
      purchase status, or store availability.
    </p>

    <h2 className={sectionClass}>Your Content</h2>
    <p className={paragraphClass}>
      You retain ownership of the documents, images, signatures, annotations, and other content
      you process with the app.
    </p>
    <p className={paragraphClass}>
      You are responsible for ensuring that you have all rights, permissions, and legal authority
      needed to use, edit, sign, share, or export that content.
    </p>
    <p className={paragraphClass}>
      You are also responsible for maintaining backup copies of important files and for reviewing
      all outputs before relying on them.
    </p>

    <h2 className={sectionClass}>Acceptable Use</h2>
    <p className={paragraphClass}>
      You may not use the app to violate law, infringe intellectual property or privacy rights,
      distribute malware, interfere with the app&apos;s operation, bypass purchase controls, or
      reverse engineer the app except where applicable law expressly allows it despite this limitation.
    </p>
    <p className={paragraphClass}>
      You may not use the app in a way that could damage, disable, overburden, or impair the app
      or related providers.
    </p>

    <h2 className={sectionClass}>Purchases and Billing</h2>
    <p className={paragraphClass}>
      Some features require a paid subscription or purchase. Billing is handled by Apple, Google,
      RevenueCat, and related payment infrastructure, not directly by Appazzola.
    </p>
    <p className={paragraphClass}>
      Prices, subscription terms, free trials, renewal timing, cancellation, and refunds are
      controlled by the relevant app store rules and the offer presented to you at the time of purchase.
    </p>
    <p className={paragraphClass}>
      If you delete the app, that does not automatically cancel a subscription. You must manage
      subscriptions through the app store account used to purchase them.
    </p>
    <p className={paragraphClass}>
      Store providers may impose additional purchase, billing, cancellation, refund,
      family-sharing, or subscription-renewal rules that also apply to you.
    </p>

    <h2 className={sectionClass}>Intellectual Property</h2>
    <p className={paragraphClass}>
      The app, including its design, software, branding, text, graphics, and related materials,
      is owned by Appazzola or its licensors and is protected by applicable intellectual-property laws.
    </p>
    <p className={paragraphClass}>
      These Terms give you a limited, revocable, non-exclusive, non-transferable right to use
      the app for its intended purpose, subject to these Terms and any applicable store rules.
    </p>

    <h2 className={sectionClass}>No Professional Advice</h2>
    <p className={paragraphClass}>
      The app is a general-purpose document utility. It does not provide legal, tax, compliance,
      records-management, or professional advice, and an electronic signature created with the app
      may not satisfy every legal or industry-specific requirement.
    </p>

    <h2 className={sectionClass}>Availability and Changes</h2>
    <p className={paragraphClass}>
      We do not guarantee that the app or any feature will always be available, error-free,
      secure, or compatible with every device, file, format, file type, or workflow.
    </p>
    <p className={paragraphClass}>
      We may suspend or discontinue all or part of the app at any time.
    </p>
    <p className={paragraphClass}>
      Maintenance, outages, third-party service interruptions, store issues, or device-specific
      issues may affect availability.
    </p>

    <h2 className={sectionClass}>Disclaimers</h2>
    <p className={paragraphClass}>
      To the maximum extent permitted by law, the app is provided on an &quot;as is&quot; and
      &quot;as available&quot; basis, without warranties of any kind, whether express, implied,
      or statutory, including implied warranties of merchantability, fitness for a particular purpose,
      title, and non-infringement.
    </p>
    <p className={paragraphClass}>
      You are responsible for reviewing outputs before relying on them, sharing them, or using
      them in a business, legal, medical, financial, or archival context.
    </p>
    <p className={paragraphClass}>
      You assume the risk of any action you take based on app output or document changes made with the app.
    </p>

    <h2 className={sectionClass}>Limitation of Liability</h2>
    <p className={paragraphClass}>
      To the maximum extent permitted by law, Appazzola and its affiliates, licensors, and service
      providers will not be liable for any indirect, incidental, special, consequential, exemplary,
      or punitive damages, or for any loss of profits, revenue, data, goodwill, or business
      opportunities arising from or related to your use of the app.
    </p>
    <p className={paragraphClass}>
      To the maximum extent permitted by law, the total liability of Appazzola for all claims
      arising out of or relating to the app will not exceed the greater of the amount you paid
      for the app in the 12 months before the claim arose or 25 U.S. dollars.
    </p>

    <h2 className={sectionClass}>Termination</h2>
    <p className={paragraphClass}>
      We may suspend or terminate your access to paid or unpaid features if we reasonably believe
      you violated these Terms, created risk for users or providers, or used the app in a prohibited way.
    </p>
    <p className={paragraphClass}>You may stop using the app at any time.</p>
    <p className={paragraphClass}>
      Termination or discontinuation does not necessarily affect rights or obligations that by
      their nature should survive, including ownership, disclaimer, liability, payment, and
      dispute-related provisions.
    </p>

    <h2 className={sectionClass}>Governing Law</h2>
    <p className={paragraphClass}>
      Unless applicable law requires otherwise, these Terms are governed by the laws of the
      jurisdiction in which Appazzola is established, without regard to conflict-of-laws rules.
    </p>

    <h2 className={sectionClass}>Changes to These Terms</h2>
    <p className={paragraphClass}>
      We may update these Terms from time to time. Continued use of the app after an update
      becomes effective means you accept the revised Terms.
    </p>

    <h2 className={sectionClass}>Contact</h2>
    <p className={paragraphClass}>
      For questions about these Terms, contact{" "}
      <a className="text-teal-600 underline" href="mailto:contact@appazzola.com">
        contact@appazzola.com
      </a>
      .
    </p>
  </div>
);

export default TermsPDFTools;
